/**
 * GA4 Event Types for dispatchGTMEvent
 *
 * These types define the structure of GA4 events for type-safe event dispatching.
 * Currency uses string type to avoid dependency on @skin-seoul/service.
 */

// GA4 Payment Types
export type GA4PaymentType = string;

// GA4 Checkout Types
export type GA4CheckoutType = 'express' | 'normal';

// GA4 User Types
export type GA4UserType = 'guest' | 'user';

// GA4 Platform Types (web for browser, app for webview)
export type GA4Platform = 'web' | 'app';

// GA4 Item structure (GA4 ecommerce standard)
export type GA4Item = {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_sku: string;
  item_category?: string;
  price: number;
  quantity: number;
};

// GA4 User object
export type GA4User = {
  type: GA4UserType;
  email?: string;
};

// GA4 Express Checkout object
export type GA4ExpressCheckoutUser = {
  email?: string;
};

export type GA4ExpressCheckout = {
  user?: GA4ExpressCheckoutUser;
  button_shown: string[];
  checkout_step: 'payment_click' | 'payment_success';
};

// GA4 Ecommerce object for purchase event
export type GA4PurchaseEcommerce = {
  transaction_id: string;
  currency: string;
  value: number;
  tax: number;
  shipping: number;
  payment_type: GA4PaymentType;
  checkout_type: GA4CheckoutType;
  items: GA4Item[];
};

// GA4 Ecommerce object for click event
export type GA4ClickEcommerce = {
  currency: string;
  value: number;
  payment_type: GA4PaymentType;
  checkout_type: 'express';
  items: GA4Item[];
};

// GA4 Purchase Event Parameters
export type GA4PurchaseParams = {
  ecommerce: GA4PurchaseEcommerce;
  user: GA4User;
  platform: GA4Platform;
  express_checkout?: GA4ExpressCheckout;
};

// GA4 Express Checkout Click Event Parameters
export type GA4ExpressCheckoutClickParams = {
  ecommerce: GA4ClickEcommerce;
  page_type: 'checkout';
  platform: GA4Platform;
  express_checkout: GA4ExpressCheckout;
  user: GA4User;
};

// GA4 Interaction Page Types
export type GA4InteractionPageType = 'pdp' | 'brand' | 'category' | 'search';

// GA4 Login Status
export type GA4LoginStatus = 'logged_in' | 'logged_out';

// GA4 User Identity Properties
export type GA4UserIdentityProperties = {
  login_status: GA4LoginStatus;
  user_tier?: string;
};

// Discriminated union for all GA4 events
export type GA4Event =
  | ({ event: 'purchase' } & GA4PurchaseParams)
  | ({ event: 'express_checkout_click' } & GA4ExpressCheckoutClickParams)
  | { event: 'engaged_60s'; page_type: GA4InteractionPageType; page_path: string }
  | { event: 'reward_granted'; user_id: string; reward_amount: number; page_type: GA4InteractionPageType; page_path: string; reward_type: 'engagement_60s' }
  | { event: 'user_identity_ready'; user_id: string | null; user_properties: GA4UserIdentityProperties };
